import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import pb from '../lib/pocketbase';
import ConfirmModal from '../components/ConfirmModal';
import { useReferencesData } from '../context/ReferencesContext';

// DnD Imports
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// --- Composants Triables ---

function SortableRow({ item, selectedIds, toggleSelectOne, onTogglePublish, publishingId, loading, deletingId, onDelete, logoUrl }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 999 : 'auto',
    background: isDragging ? 'var(--bg-subtle)' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className={selectedIds.includes(item.id) ? 'row-selected' : ''}>
      {/* Poignée de tri */}
      <td style={{ width: '30px', cursor: 'grab', textAlign: 'center', touchAction: 'none' }} {...attributes} {...listeners} title="Maintenir pour déplacer">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)' }}>
          <circle cx="9" cy="5" r="1" />
          <circle cx="9" cy="12" r="1" />
          <circle cx="9" cy="19" r="1" />
          <circle cx="15" cy="5" r="1" />
          <circle cx="15" cy="12" r="1" />
          <circle cx="15" cy="19" r="1" />
        </svg>
      </td>
      <td style={{ textAlign: 'center', width: '40px' }}>
        <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelectOne(item.id)} />
      </td>
      <td data-label="Client">
        <div className="admin-user-cell">
          {logoUrl(item) ? (
            <img src={logoUrl(item)} alt="Logo" className="admin-user-avatar" />
          ) : (
            <div className="admin-user-avatar placeholder">{(item.client?.[0] || 'R').toUpperCase()}</div>
          )}
          <div className="admin-user-meta">
            <div className="admin-user-name">{item.client}</div>
            <div className="admin-user-id">{item.slug}</div>
          </div>
        </div>
      </td>
      <td data-label="Titre">{item.titre}</td>
      <td data-label="Année">{item.annee || '—'}</td>
      <td data-label="Statut" style={{ whiteSpace: 'nowrap' }}>
        <button
          type="button"
          className="btn"
          title={item.published ? 'Publié (cliquer pour passer en brouillon)' : 'Brouillon (cliquer pour publier)'}
          onClick={() => onTogglePublish(item)}
          disabled={publishingId === item.id || loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
        >
          {item.published ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.8 21.8 0 0 1 5.06-6.94" /><path d="M1 1l22 22" /><path d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 2.42-4.42" /><path d="M21 21A16 16 0 0 1 12 4c-1.1 0-2.16.16-3.16.46" /></svg>
          )}
          {publishingId === item.id ? '...' : (item.published ? 'Publié' : 'Brouillon')}
        </button>
      </td>
      <td data-label="Actions" style={{ whiteSpace: 'nowrap' }}>
        <Link className="btn" to={`/admin/references/${item.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
          Éditer
        </Link>
        <button
          type="button"
          className="btn"
          title="Supprimer"
          onClick={() => onDelete(item.id)}
          disabled={deletingId === item.id || loading}
          style={{ marginLeft: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
        </button>
      </td>
    </tr>
  );
}

function SortableCard({ item, selectedIds, toggleSelectOne, deletingId, onDelete, loading, logoUrl }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const [isHovered, setIsHovered] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 999 : 'auto',
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'none'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`ref-card ${selectedIds.includes(item.id) ? 'card-selected' : ''}`}
      {...attributes}
      {...listeners}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 10,
        opacity: isHovered || isDragging ? 1 : 0,
        transition: 'opacity 0.2s ease',
        background: 'var(--card-bg, #fff)',
        padding: '4px',
        borderRadius: '6px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        border: '1px solid var(--border-subtle, #e5e7eb)',
        pointerEvents: 'none'
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)' }}>
          <circle cx="9" cy="5" r="1" />
          <circle cx="9" cy="12" r="1" />
          <circle cx="9" cy="19" r="1" />
          <circle cx="15" cy="5" r="1" />
          <circle cx="15" cy="12" r="1" />
          <circle cx="15" cy="19" r="1" />
        </svg>
      </div>

      <div style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 10 }}>
        {/* Stop propagation on checkbox area to prevent drag start */}
        <div onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelectOne(item.id)} style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--accent)' }} />
        </div>
      </div>

      <div className="ref-card-media">
        {logoUrl(item) ? (
          <img src={logoUrl(item)} alt={item.client} />
        ) : (
          <div className="ref-card-placeholder">{(item.client?.[0] || 'R').toUpperCase()}</div>
        )}
      </div>
      <div className="ref-card-body">
        <h3 className="ref-card-title">{item.titre || item.client}</h3>
        <div className="ref-card-meta">{[item.client, item.annee, item.published ? 'Publié' : 'Brouillon'].filter(Boolean).join(' • ')}</div>

        <div
          style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Link className="btn btn-primary" to={`/admin/references/${item.id}`} style={{ flex: 1, justifyContent: 'center' }}>
            Éditer
          </Link>
          <button
            type="button"
            className="btn"
            title="Supprimer"
            onClick={() => onDelete(item.id)}
            disabled={deletingId === item.id || loading}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}


export default function ReferencesListPage() {
  const { references: contextRefs, loading: contextLoading, refresh } = useReferencesData();
  const [items, setItems] = useState(contextRefs || []);
  // Pagination removed: show all items
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [published, setPublished] = useState('all');
  const [sortBy, setSortBy] = useState('order'); // Default sort by order
  const [sortDir, setSortDir] = useState('asc'); // Default asc
  // Filters (Boolean checkboxes)
  const [onlyVedette, setOnlyVedette] = useState(false);
  const [onlySlider, setOnlySlider] = useState(false);
  const [onlyHiddenClient, setOnlyHiddenClient] = useState(false);

  // Init viewMode from localStorage
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('admin_ref_view') || 'list');

  useEffect(() => {
    localStorage.setItem('admin_ref_view', viewMode);
  }, [viewMode]);

  const latestReq = useRef(0);
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmer',
    isDestructive: false,
    onConfirm: () => { },
  });

  // Context Data already loaded at top

  // Sync state with context
  useEffect(() => {
    if (contextRefs) {
      setItems(contextRefs);
    }
  }, [contextRefs]);

  const filteredItems = useMemo(() => {
    let res = [...items];

    // 1. Search
    if (q.trim()) {
      const lowerQ = q.trim().toLowerCase();
      res = res.filter(i =>
        (i.client || '').toLowerCase().includes(lowerQ) ||
        (i.titre || '').toLowerCase().includes(lowerQ) ||
        (i.secteur || '').toLowerCase().includes(lowerQ)
      );
    }

    // 2. Published Status
    if (published !== 'all') {
      const isPub = published === 'true';
      res = res.filter(i => i.published === isPub);
    }

    // 3. Checkboxes
    if (onlyVedette) res = res.filter(i => i.vedette);
    if (onlySlider) res = res.filter(i => i.in_slider);
    if (onlyHiddenClient) res = res.filter(i => i.show_client === false);

    // 4. Sorting
    res.sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];

      // Handle strings case-insensitive
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return res;
  }, [items, q, published, onlyVedette, onlySlider, onlyHiddenClient, sortBy, sortDir]);

  const fetchList = async () => {
    // Just refresh context
    await refresh();
  };

  const onHandleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setItems((prevItems) => {
      const oldIndex = prevItems.findIndex((item) => item.id === active.id);
      const newIndex = prevItems.findIndex((item) => item.id === over.id);
      return arrayMove(prevItems, oldIndex, newIndex);
    });

    try {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);

      // Update all items order based on their new simple index
      const updates = newItems.map((item, index) => ({
        id: item.id,
        order: index
      }));

      await Promise.all(updates.map(u => pb.collection('references').update(u.id, { order: u.order })));

    } catch (err) {
      console.error("Erreur lors de la sauvegarde de l'ordre", err);
      fetchList(); // Revert on error
    }
  };

  const onTogglePublish = async (it) => {
    if (!it?.id) return;
    try {
      setPublishingId(it.id);
      const next = !it.published;
      await pb.collection('references').update(it.id, { published: next });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, published: next } : x)));
    } catch (err) {
      alert('Mise à jour impossible: ' + (err?.message));
    } finally {
      setPublishingId(null);
    }
  };


  const logoUrl = (r) => (r?.logo ? pb.files.getUrl(r, r.logo, { thumb: '128x128' }) : null);

  const onDelete = async (id) => {
    if (!id) return;
    setModalConfig({
      isOpen: true,
      title: 'Supprimer la référence ?',
      message: 'Cette action est irréversible.',
      confirmLabel: 'Supprimer',
      isDestructive: true,
      onConfirm: async () => {
        try {
          setDeletingId(id);
          await pb.collection('references').delete(id);
          setItems((prev) => prev.filter((it) => it.id !== id));
        } catch (err) {
          alert('Erreur: ' + err.message);
        } finally {
          setDeletingId(null);
        }
      }
    });
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === items.length) setSelectedIds([]);
    else setSelectedIds(items.map((r) => r.id));
  };

  const handleBatchAction = async (action) => {
    if (!selectedIds.length) return;

    const runBatch = async () => {
      setBatchLoading(true);
      try {
        const promises = selectedIds.map((id) => {
          if (action === 'delete') return pb.collection('references').delete(id);
          if (action === 'publish') return pb.collection('references').update(id, { published: true });
          if (action === 'brouillon') return pb.collection('references').update(id, { published: false });
          if (action === 'show_client') return pb.collection('references').update(id, { show_client: true });
          if (action === 'hide_client') return pb.collection('references').update(id, { show_client: false });
          if (action === 'vedette') return pb.collection('references').update(id, { vedette: true });
          if (action === 'unvedette') return pb.collection('references').update(id, { vedette: false });
          if (action === 'in_slider') return pb.collection('references').update(id, { in_slider: true });
          if (action === 'out_slider') return pb.collection('references').update(id, { in_slider: false });
          return Promise.resolve();
        });
        await Promise.all(promises);
        fetchList();
        setSelectedIds([]);
      } catch (err) {
        alert('Erreur batch: ' + err.message);
      } finally {
        setBatchLoading(false);
      }
    };

    if (action === 'delete') {
      setModalConfig({
        isOpen: true,
        title: 'Supprimer la sélection ?',
        message: `Supprimer ${selectedIds.length} élément(s) ?`,
        confirmLabel: 'Tout supprimer',
        isDestructive: true,
        onConfirm: runBatch
      });
    } else {
      runBatch();
    }
  };

  return (
    <section className="section">
      <div className="section-inner">
        <div className="section-header">
          <h2>Références</h2>
          <div className="admin-users-controls" style={{ gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="search" placeholder="Rechercher..." value={q} onChange={(e) => { setQ(e.target.value); }} style={{ maxWidth: '200px' }} />

            <select value={published} onChange={(e) => { setPublished(e.target.value); }} title="Statut Publication">
              <option value="all">Tout Statut</option>
              <option value="true">Publiés</option>
              <option value="false">Brouillons</option>
            </select>

            {/* Checkbox Filters */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={onlyVedette} onChange={(e) => setOnlyVedette(e.target.checked)} style={{ width: 'auto', margin: 0, accentColor: 'var(--accent)' }} />
              Vedette
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={onlySlider} onChange={(e) => setOnlySlider(e.target.checked)} style={{ width: 'auto', margin: 0, accentColor: 'var(--accent)' }} />
              Slider Confiance
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={onlyHiddenClient} onChange={(e) => setOnlyHiddenClient(e.target.checked)} style={{ width: 'auto', margin: 0, accentColor: 'var(--accent)' }} />
              Client Masqué
            </label>

            <div className="segmented" style={{ marginRight: 'auto' }}>
              <button type="button" className={`seg ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg></button>
              <button type="button" className={`seg ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg></button>
            </div>
            <button className="btn" type="button" onClick={() => navigate('/admin/references/new')}>Ajouter</button>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="admin-batch-toolbar" style={{ margin: '0 0 1rem 0', padding: '0.75rem', background: 'var(--card-bg)', border: '1px solid var(--accent)', borderRadius: '8px', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{selectedIds.length} sélectionné(s)</span>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSelectedIds([])} style={{ padding: '0 0.5rem', height: '24px', minHeight: 'auto', fontSize: '0.8rem' }}>Désélectionner</button>
            <div style={{ height: '20px', width: '1px', background: 'var(--border-subtle)', margin: '0 0.5rem' }}></div>
            <button type="button" className="btn btn-sm btn-ghost" disabled={batchLoading} onClick={() => handleBatchAction('publish')}>Publier</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={batchLoading} onClick={() => handleBatchAction('brouillon')}>Brouillon</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={batchLoading} onClick={() => handleBatchAction('show_client')}>Afficher Client</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={batchLoading} onClick={() => handleBatchAction('hide_client')}>Masquer Client</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={batchLoading} onClick={() => handleBatchAction('vedette')}>Mettre en Vedette</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={batchLoading} onClick={() => handleBatchAction('unvedette')}>Retirer Vedette</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={batchLoading} onClick={() => handleBatchAction('in_slider')}>Ajouter Slider Confiance</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={batchLoading} onClick={() => handleBatchAction('out_slider')}>Retirer Slider Confiance</button>
            <div style={{ flex: 1 }}></div>
            <button type="button" className="btn btn-sm" style={{ background: '#ef4444', color: 'white', border: 'none' }} disabled={batchLoading} onClick={() => handleBatchAction('delete')}>
              Supprimer
            </button>
          </div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onHandleDragEnd}>
          <div className={viewMode === 'list' ? 'card' : ''}>
            {error ? <div className="alert error">{error}</div> : null}
            {loading ? (
              <div className="admin-table-loading">Chargement…</div>
            ) : viewMode === 'list' ? (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: '30px' }}></th>
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input type="checkbox" checked={filteredItems.length > 0 && selectedIds.length === filteredItems.length} onChange={toggleSelectAll} disabled={filteredItems.length === 0} />
                      </th>
                      <th>Client</th>
                      <th>Titre</th>
                      <th>
                        <button type="button" className={`admin-th-btn ${sortBy === 'annee' ? 'active' : ''}`} onClick={() => { setSortBy('annee'); setSortDir((d) => sortBy === 'annee' ? (d === 'asc' ? 'desc' : 'asc') : 'desc'); }}>Année</button>
                      </th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <SortableContext items={filteredItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    <tbody>
                      {filteredItems.length === 0 ? (
                        <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Aucune référence</td></tr>
                      ) : (
                        filteredItems.map((r) => (
                          <SortableRow
                            key={r.id}
                            item={r}
                            selectedIds={selectedIds}
                            toggleSelectOne={toggleSelectOne}
                            onTogglePublish={onTogglePublish}
                            publishingId={publishingId}
                            loading={loading}
                            deletingId={deletingId}
                            onDelete={onDelete}
                            logoUrl={logoUrl}
                          />
                        ))
                      )}
                    </tbody>
                  </SortableContext>
                </table>
              </div>
            ) : (
              <SortableContext items={filteredItems.map(i => i.id)} strategy={rectSortingStrategy}>
                <div className="references-grid uniform" style={{ padding: '0.5rem' }}>
                  {filteredItems.length === 0 ? (
                    <div className="card" style={{ gridColumn: '1 / -1', color: 'var(--text-muted)', textAlign: 'center' }}>Aucune référence</div>
                  ) : (
                    filteredItems.map((r) => (
                      <SortableCard
                        key={r.id}
                        item={r}
                        selectedIds={selectedIds}
                        toggleSelectOne={toggleSelectOne}
                        deletingId={deletingId}
                        loading={loading}
                        onDelete={onDelete}
                        logoUrl={logoUrl}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            )}
            <div className="admin-table-footer">
              <span className="admin-page-info">{filteredItems.length} référence(s)</span>
            </div>
          </div>
        </DndContext>
      </div>

      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmLabel={modalConfig.confirmLabel}
        isDestructive={modalConfig.isDestructive}
        onConfirm={modalConfig.onConfirm}
        onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
      />
    </section>
  );
}
